import React, { useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";

// GSM Terminal -- a shared, no-personal-login kiosk. A player types their
// surname, the system matches them against the real roster (GET
// /api/terminal/:slug/search), and from there they can register themselves
// as playing in an upcoming competition or enter their own hole-by-hole
// score for one they're already registered in. Every write is re-validated
// server-side against this exact player/event pairing -- see terminal.js --
// since a surname match is the only "authentication" this kiosk has.
export default function Terminal() {
  const { slug } = useParams();

  const [surname, setSurname] = useState("");
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [player, setPlayer] = useState(null);

  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);

  const [scoreEvent, setScoreEvent] = useState(null);
  const [holes, setHoles] = useState([]);
  const [partnerName, setPartnerName] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const [busyEventId, setBusyEventId] = useState(null);
  const [error, setError] = useState("");

  // General Play -- a member-organised round outside any competition.
  // gpScreen: null | 'preview' | 'registered' | 'score' | 'review' | 'done'
  const [gpInfo, setGpInfo] = useState(null);
  const [gpScreen, setGpScreen] = useState(null);
  const [gpPreview, setGpPreview] = useState(null);
  const [gpRound, setGpRound] = useState(null);
  const [gpHoles, setGpHoles] = useState([]);
  const [gpBusy, setGpBusy] = useState(false);
  const [gpError, setGpError] = useState("");

  const search = async (q) => {
    setSurname(q);
    if (q.trim().length < 2) { setMatches([]); return; }
    setSearching(true);
    try {
      const res = await api.get(`/terminal/${slug}/search`, { params: { q } });
      setMatches(res.data);
    } catch (e) { setMatches([]); }
    finally { setSearching(false); }
  };

  const loadEvents = async (p) => {
    setPlayer(p); setMatches([]); setError("");
    setLoadingEvents(true);
    try {
      const res = await api.get(`/terminal/${slug}/events`, { params: { player_id: p.id } });
      setEvents(res.data);
    } catch (e) { setError("Couldn't load competitions — please try again."); }
    finally { setLoadingEvents(false); }
    api.get(`/terminal/${slug}/general-play/info`).then(res => setGpInfo(res.data)).catch(() => setGpInfo(null));
  };

  // ---- General Play ----

  const gpStartRegister = async () => {
    setGpError(""); setGpBusy(true);
    try {
      const res = await api.get(`/terminal/${slug}/general-play/preview`, { params: { player_id: player.id } });
      setGpPreview(res.data);
      setGpScreen("preview");
    } catch (e) { setGpError(e.response?.data?.error || "Couldn't load your details — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpConfirmRegister = async () => {
    setGpBusy(true); setGpError("");
    try {
      const res = await api.post(`/terminal/${slug}/general-play/register`, { player_id: player.id });
      setGpRound(res.data);
      setGpScreen("registered");
    } catch (e) { setGpError(e.response?.data?.error || "Registration failed — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpStartScore = async () => {
    setGpError(""); setGpBusy(true);
    try {
      const res = await api.get(`/terminal/${slug}/general-play/mine`, { params: { player_id: player.id } });
      setGpRound(res.data);
      setGpHoles(res.data.holes);
      setGpScreen("score");
    } catch (e) { setGpError(e.response?.data?.error || "No registered General Play round found — register a round first."); }
    finally { setGpBusy(false); }
  };

  const gpSetHole = (hole, value) => {
    const gross = value === "" ? null : parseInt(value, 10);
    setGpHoles(prev => prev.map(h => h.hole === hole ? { ...h, gross } : h));
  };

  const gpSaveAndReview = async () => {
    setGpBusy(true); setGpError("");
    try {
      const scores = gpHoles.filter(h => Number.isInteger(h.gross) && h.gross >= 1 && h.gross <= 20).map(h => ({ hole: h.hole, gross: h.gross }));
      await api.post(`/terminal/${slug}/general-play/save`, { player_id: player.id, round_id: gpRound.round_id, scores });
      setGpScreen("review");
    } catch (e) { setGpError(e.response?.data?.error || "Couldn't save — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpSubmit = async () => {
    setGpBusy(true); setGpError("");
    try {
      await api.post(`/terminal/${slug}/general-play/submit`, { player_id: player.id, round_id: gpRound.round_id });
      setGpScreen("done");
    } catch (e) { setGpError(e.response?.data?.error || "Couldn't submit — please try again."); }
    finally { setGpBusy(false); }
  };

  const gpReset = () => { setGpScreen(null); setGpPreview(null); setGpRound(null); setGpHoles([]); setGpError(""); };

  const register = async (eventId) => {
    setBusyEventId(eventId); setError("");
    try {
      await api.post(`/terminal/${slug}/register`, { player_id: player.id, event_id: eventId });
      setEvents(prev => prev.map(ev => ev.id === eventId ? { ...ev, already_registered: true } : ev));
    } catch (e) { setError(e.response?.data?.error || "Registration failed — please try again."); }
    finally { setBusyEventId(null); }
  };

  const openScorecard = async (ev) => {
    setScoreEvent(ev); setSaved(false); setError("");
    setLoadingCard(true);
    try {
      const res = await api.get(`/terminal/${slug}/scorecard`, { params: { player_id: player.id, event_id: ev.id } });
      setHoles(res.data.holes);
      setPartnerName(res.data.partner_name || null);
    } catch (e) { setError("Couldn't load your scorecard — please try again."); setScoreEvent(null); }
    finally { setLoadingCard(false); }
  };

  const setHoleScore = (hole, value) => {
    const gross = value === "" ? null : parseInt(value, 10);
    setHoles(prev => prev.map(h => h.hole === hole ? { ...h, gross } : h));
  };

  const saveScores = async () => {
    setSaving(true); setError("");
    try {
      const scores = holes
        .filter(h => !h.locked && Number.isInteger(h.gross) && h.gross >= 1 && h.gross <= 20)
        .map(h => ({ hole: h.hole, gross: h.gross }));
      await api.post(`/terminal/${slug}/scores/save`, { player_id: player.id, event_id: scoreEvent.id, scores });
      setSaved(true);
    } catch (e) { setError(e.response?.data?.error || "Couldn't save — please try again."); }
    finally { setSaving(false); }
  };

  const Card = ({ children, wide }) => (
    <div style={{ background: "white", borderRadius: "16px", padding: "28px 24px", maxWidth: wide ? "600px" : "420px", width: "100%", boxShadow: "0 4px 24px rgba(0,0,0,0.10)" }}>{children}</div>
  );
  const Page = ({ children, wide }) => (
    <div style={{ minHeight: "100vh", background: "#f0fdf4", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <Card wide={wide}>{children}</Card>
    </div>
  );
  const backBtn = (onClick, label) => (
    <button onClick={onClick} style={{ background: "none", border: "none", color: "#166534", fontSize: "13px", fontWeight: 600, cursor: "pointer", padding: 0, marginBottom: "16px" }}>
      ← {label}
    </button>
  );

  // General Play screens
  if (gpScreen === "preview" && gpPreview) {
    return (
      <Page>
        {backBtn(gpReset, "Back")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "16px" }}>General Play</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginBottom: "20px" }}>
          {[["Name", gpPreview.player_name], ["Club", gpPreview.society_name], ["Course", gpPreview.course_name], ["Current Handicap", gpPreview.handicap], ["Date", new Date().toLocaleDateString("en-GB")]].map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: "14px", padding: "8px 0", borderBottom: "1px solid #f3f4f6" }}>
              <span style={{ color: "#6b7280" }}>{label}</span><span style={{ fontWeight: 700, color: "#111827" }}>{value}</span>
            </div>
          ))}
        </div>
        <button onClick={gpConfirmRegister} disabled={gpBusy}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
          {gpBusy ? "Confirming…" : "Confirm General Play Round"}
        </button>
      </Page>
    );
  }

  if (gpScreen === "registered" && gpRound) {
    return (
      <Page>
        <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>✓</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "10px", textAlign: "center" }}>{gpRound.player_name}</div>
        <div style={{ fontSize: "14px", color: "#374151", textAlign: "center", marginBottom: "20px" }}>
          Your General Play round has been registered. Please return to this kiosk after your round to enter your score.
        </div>
        <button onClick={() => { gpReset(); setPlayer(null); }}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Done
        </button>
      </Page>
    );
  }

  if (gpScreen === "score") {
    return (
      <Page wide>
        {backBtn(gpReset, "Back")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "2px" }}>{gpRound.course_name} — General Play</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>{gpRound.player_name} — {new Date(gpRound.registered_at).toLocaleDateString("en-GB")}</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px", marginBottom: "20px" }}>
          {gpHoles.map(h => (
            <div key={h.hole} style={{ textAlign: "center" }}>
              <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px" }}>Hole {h.hole}</div>
              <div style={{ fontSize: "10px", color: "#9ca3af", marginBottom: "4px" }}>Par {h.par}</div>
              <input type="number" min="1" max="20" value={h.gross ?? ""} onChange={e => gpSetHole(h.hole, e.target.value)}
                style={{ width: "100%", textAlign: "center", padding: "10px 4px", fontSize: "16px", fontWeight: 700, border: "1.5px solid #d1d5db", borderRadius: "8px", boxSizing: "border-box" }} />
            </div>
          ))}
        </div>
        <button onClick={gpSaveAndReview} disabled={gpBusy}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
          {gpBusy ? "Saving…" : "Finish"}
        </button>
      </Page>
    );
  }

  if (gpScreen === "review") {
    const total = gpHoles.reduce((sum, h) => sum + (Number.isInteger(h.gross) ? h.gross : 0), 0);
    return (
      <Page wide>
        {backBtn(() => setGpScreen("score"), "Back to entry")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "16px" }}>Review Your Scorecard</div>
        {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{gpError}</div>}
        <table style={{ width: "100%", fontSize: "13px", marginBottom: "16px", borderCollapse: "collapse" }}>
          <thead><tr style={{ textAlign: "left", color: "#6b7280" }}><th>Hole</th><th>Par</th><th>Gross</th></tr></thead>
          <tbody>
            {gpHoles.map(h => (
              <tr key={h.hole} style={{ borderTop: "1px solid #f3f4f6" }}><td>{h.hole}</td><td>{h.par}</td><td>{h.gross ?? "—"}</td></tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontWeight: 700, fontSize: "15px", marginBottom: "16px" }}>Total gross: {total}</div>
        <button onClick={gpSubmit} disabled={gpBusy}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
          {gpBusy ? "Submitting…" : "Submit General Play Score"}
        </button>
      </Page>
    );
  }

  if (gpScreen === "done") {
    return (
      <Page>
        <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>✓</div>
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "10px", textAlign: "center" }}>Scores Saved</div>
        <div style={{ fontSize: "14px", color: "#374151", textAlign: "center", marginBottom: "20px" }}>Your General Play round has been recorded.</div>
        <button onClick={() => { gpReset(); setPlayer(null); }}
          style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer" }}>
          Done
        </button>
      </Page>
    );
  }

  // Screen 3: score entry
  if (scoreEvent) {
    return (
      <Page wide>
        {backBtn(() => { setScoreEvent(null); setSaved(false); }, "Back to competitions")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "2px" }}>{scoreEvent.venue}</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>{player.name} — {new Date(scoreEvent.date).toLocaleDateString("en-GB")}</div>
        {partnerName && (
          <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8", fontSize: "13px", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px" }}>
            You and {partnerName} share one ball in this format — enter your pair's single score per hole.
          </div>
        )}
        {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
        {saved && <div style={{ background: "#f0fdf4", color: "#166534", fontSize: "13px", padding: "10px 14px", borderRadius: "8px", marginBottom: "12px" }}>✓ Scores saved.</div>}
        {loadingCard ? (
          <div style={{ color: "#6b7280", fontSize: "13px", textAlign: "center", padding: "20px" }}>Loading scorecard…</div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "8px", marginBottom: "20px" }}>
              {holes.map(h => (
                <div key={h.hole} style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "2px" }}>Hole {h.hole}</div>
                  <div style={{ fontSize: "10px", color: "#9ca3af", marginBottom: "4px" }}>Par {h.par}</div>
                  <input
                    type="number" min="1" max="20"
                    value={h.gross ?? ""}
                    disabled={h.locked}
                    onChange={e => setHoleScore(h.hole, e.target.value)}
                    style={{
                      width: "100%", textAlign: "center", padding: "10px 4px", fontSize: "16px", fontWeight: 700,
                      border: h.locked ? "1.5px solid #d1fae5" : "1.5px solid #d1d5db", borderRadius: "8px",
                      background: h.locked ? "#f0fdf4" : "white", color: h.locked ? "#166534" : "#111827",
                      boxSizing: "border-box",
                    }}
                  />
                  {h.locked && <div style={{ fontSize: "9px", color: "#16a34a", marginTop: "2px" }}>✓ verified</div>}
                </div>
              ))}
            </div>
            <button onClick={saveScores} disabled={saving}
              style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "10px", padding: "13px", fontSize: "15px", fontWeight: 700, cursor: "pointer", opacity: saving ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save Scores"}
            </button>
          </>
        )}
      </Page>
    );
  }

  // Screen 2: events list for this player
  if (player) {
    return (
      <Page>
        {backBtn(() => setPlayer(null), "Search again")}
        <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px" }}>Hi {player.name.split(" ")[0]}</div>
        <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "16px" }}>Choose a competition below.</div>
        {error && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "12px" }}>{error}</div>}
        {loadingEvents ? (
          <div style={{ color: "#6b7280", fontSize: "13px", textAlign: "center", padding: "20px" }}>Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ color: "#6b7280", fontSize: "13px" }}>No upcoming competitions right now.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {events.map(ev => (
              <div key={ev.id} style={{ padding: "14px", border: "1px solid #d1fae5", borderRadius: "10px" }}>
                <div style={{ fontWeight: 700, fontSize: "14px", color: "#14532d" }}>{ev.venue}</div>
                <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "10px" }}>
                  {new Date(ev.date).toLocaleDateString("en-GB")} · {ev.game_type}
                </div>
                {ev.already_registered ? (
                  <button onClick={() => openScorecard(ev)}
                    style={{ width: "100%", background: "#1d4ed8", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                    Enter Score
                  </button>
                ) : (
                  <button onClick={() => register(ev.id)} disabled={busyEventId === ev.id}
                    style={{ width: "100%", background: "#166534", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer", opacity: busyEventId === ev.id ? 0.6 : 1 }}>
                    {busyEventId === ev.id ? "Registering…" : "Register — I'm Playing"}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        {gpInfo && gpInfo.enabled && (
          <div style={{ marginTop: "18px", paddingTop: "16px", borderTop: "1px solid #e5e7eb" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, color: "#14532d", marginBottom: "8px" }}>General Play — {gpInfo.course_name}</div>
            {gpError && <div style={{ color: "#b91c1c", fontSize: "13px", marginBottom: "8px" }}>{gpError}</div>}
            <div style={{ display: "flex", gap: "8px" }}>
              <button onClick={gpStartRegister} disabled={gpBusy}
                style={{ flex: 1, background: "#166534", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
                Register Round
              </button>
              <button onClick={gpStartScore} disabled={gpBusy}
                style={{ flex: 1, background: "#1d4ed8", color: "white", border: "none", borderRadius: "8px", padding: "10px", fontSize: "13px", fontWeight: 700, cursor: "pointer", opacity: gpBusy ? 0.6 : 1 }}>
                Enter Score
              </button>
            </div>
          </div>
        )}
      </Page>
    );
  }

  // Screen 1: surname search
  return (
    <Page>
      <div style={{ fontSize: "40px", textAlign: "center", marginBottom: "8px" }}>⛳</div>
      <div style={{ fontSize: "18px", fontWeight: 700, color: "#14532d", marginBottom: "4px", textAlign: "center" }}>Golf Society Manager</div>
      <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "20px", textAlign: "center" }}>Type your surname to get started</div>
      <input
        type="text" value={surname} onChange={e => search(e.target.value)} placeholder="Surname"
        autoFocus
        style={{ width: "100%", border: "1.5px solid #d1d5db", borderRadius: "10px", padding: "13px 14px", fontSize: "16px", boxSizing: "border-box", marginBottom: "12px" }}
      />
      {searching && <div style={{ color: "#9ca3af", fontSize: "12px" }}>Searching…</div>}
      {matches.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          {matches.map(m => (
            <button key={m.id} onClick={() => loadEvents(m)}
              style={{ textAlign: "left", background: "#f0fdf4", border: "1px solid #d1fae5", borderRadius: "8px", padding: "12px 14px", fontSize: "14px", fontWeight: 600, color: "#14532d", cursor: "pointer" }}>
              {m.name}
            </button>
          ))}
        </div>
      )}
      {surname.trim().length >= 2 && !searching && matches.length === 0 && (
        <div style={{ color: "#9ca3af", fontSize: "12px" }}>No match found — check the spelling, or ask your organiser to add you.</div>
      )}
    </Page>
  );
}
